/**
 * CreateContactModal - Modal for creating/editing CRM contacts
 * 
 * Features:
 * - Create new contact
 * - Edit existing contact
 * - Role dropdown (Owner, Executive, Decision Maker, etc.)
 * - Preferred contact method dropdown
 * - Optional account linking
 * - Shipping address for Ship-To contacts
 */
import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2, User, Building2 } from 'lucide-react';

// Contact role options
const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'executive', label: 'Executive' },
  { value: 'decision_maker', label: 'Decision Maker' },
  { value: 'manager', label: 'Manager' },
  { value: 'salesperson', label: 'Salesperson' },
  { value: 'assistant', label: 'Assistant' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' },
];

// Preferred contact method options
const CONTACT_METHOD_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'text', label: 'Text/SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'in_person', label: 'In Person' },
  { value: 'none', label: 'No Preference' },
];

const CreateContactModal = ({
  isOpen,
  onClose,
  onSuccess,
  editContact = null, // Pass contact object to edit
  prefilledAccountId = null, // Pre-select account
  accounts = [], // List of accounts for dropdown
}) => {
  const isEditMode = !!editContact;
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
    role_type: '',
    role_custom: '',
    preferred_contact_method: '',
    account_id: prefilledAccountId || '',
    is_primary: false,
    is_default_ship_to: false,
    notes: '',
    // Shipping address
    shipping_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'USA',
    },
    delivery_notes: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [showShippingAddress, setShowShippingAddress] = useState(false);
  
  // Reset form when modal opens/closes or edit contact changes
  useEffect(() => {
    if (isOpen) {
      if (editContact) {
        // Populate form with existing contact data
        setFormData({
          first_name: editContact.first_name || '',
          last_name: editContact.last_name || '',
          email: editContact.email || '',
          phone: editContact.phone || '',
          title: editContact.title || '',
          role_type: editContact.role_type || '',
          role_custom: editContact.role_custom || '',
          preferred_contact_method: editContact.preferred_contact_method || '',
          account_id: editContact.account_id || prefilledAccountId || '',
          is_primary: editContact.is_primary || false,
          is_default_ship_to: editContact.is_default_ship_to || false,
          notes: editContact.notes || '',
          shipping_address: editContact.shipping_address || {
            address_line1: '',
            address_line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'USA',
          },
          delivery_notes: editContact.delivery_notes || '',
        });
        // Show shipping address section if it has data
        setShowShippingAddress(!!editContact.shipping_address?.address_line1);
      } else {
        // Reset for new contact
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          title: '',
          role_type: '',
          role_custom: '',
          preferred_contact_method: '',
          account_id: prefilledAccountId || '',
          is_primary: false,
          is_default_ship_to: false,
          notes: '',
          shipping_address: {
            address_line1: '',
            address_line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: 'USA',
          },
          delivery_notes: '',
        });
        setShowShippingAddress(false);
      }
    }
  }, [isOpen, editContact, prefilledAccountId]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleShippingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      shipping_address: { ...prev.shipping_address, [field]: value }
    }));
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!formData.first_name && !formData.last_name) {
      toast.error('At least first name or last name is required');
      return;
    }
    
    setLoading(true);
    try {
      // Prepare data
      const submitData = { ...formData };
      
      // Only include shipping address if it has data
      if (!showShippingAddress || !submitData.shipping_address?.address_line1) {
        submitData.shipping_address = null;
      }
      
      // Handle empty strings for optional fields
      if (!submitData.account_id) submitData.account_id = null;
      if (!submitData.role_type) submitData.role_type = null;
      if (!submitData.preferred_contact_method) submitData.preferred_contact_method = null;
      if (submitData.role_type !== 'other') submitData.role_custom = null;
      
      let result;
      if (isEditMode) {
        result = await crmAPI.updateContact(editContact.id, submitData);
        toast.success('Contact updated successfully');
      } else {
        result = await crmAPI.createContact(submitData);
        toast.success('Contact created successfully');
      }
      
      onSuccess?.(result.contact);
      onClose();
    } catch (err) {
      console.error('Failed to save contact:', err);
      toast.error(err.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {isEditMode ? 'Edit Contact' : 'Create New Contact'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 border-b pb-2">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="John"
                  data-testid="contact-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Doe"
                  data-testid="contact-last-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="john@example.com"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="contact-phone"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Sales Manager"
                  data-testid="contact-title"
                />
              </div>
              <div>
                <Label htmlFor="role_type">Role</Label>
                <Select
                  value={formData.role_type || ''}
                  onValueChange={(value) => handleChange('role_type', value)}
                >
                  <SelectTrigger data-testid="contact-role-select">
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Custom role input when "Other" is selected */}
            {formData.role_type === 'other' && (
              <div>
                <Label htmlFor="role_custom">Custom Role</Label>
                <Input
                  id="role_custom"
                  value={formData.role_custom}
                  onChange={(e) => handleChange('role_custom', e.target.value)}
                  placeholder="Enter custom role..."
                  data-testid="contact-role-custom"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferred_contact_method">Preferred Contact Method</Label>
                <Select
                  value={formData.preferred_contact_method || ''}
                  onValueChange={(value) => handleChange('preferred_contact_method', value)}
                >
                  <SelectTrigger data-testid="contact-method-select">
                    <SelectValue placeholder="Select method..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_METHOD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="account_id">Company / Account</Label>
                <Select
                  value={formData.account_id || 'none'}
                  onValueChange={(value) => handleChange('account_id', value === 'none' ? '' : value)}
                >
                  <SelectTrigger data-testid="contact-account-select">
                    <SelectValue placeholder="Select account..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Account (Unassigned)</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name || acc.company_name || acc.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {/* Flags */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_primary"
                checked={formData.is_primary}
                onCheckedChange={(checked) => handleChange('is_primary', checked)}
                data-testid="contact-is-primary"
              />
              <Label htmlFor="is_primary" className="cursor-pointer">Primary Contact</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_default_ship_to"
                checked={formData.is_default_ship_to}
                onCheckedChange={(checked) => handleChange('is_default_ship_to', checked)}
                data-testid="contact-is-default-shipto"
              />
              <Label htmlFor="is_default_ship_to" className="cursor-pointer">Default Ship-To</Label>
            </div>
          </div>
          
          {/* Shipping Address Toggle */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="show_shipping"
                checked={showShippingAddress}
                onCheckedChange={setShowShippingAddress}
              />
              <Label htmlFor="show_shipping" className="cursor-pointer">Add Shipping Address (Ship-To)</Label>
            </div>
            
            {showShippingAddress && (
              <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Shipping Address</h4>
                
                <div>
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    value={formData.shipping_address.address_line1}
                    onChange={(e) => handleShippingChange('address_line1', e.target.value)}
                    placeholder="123 Main St"
                    data-testid="contact-address1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={formData.shipping_address.address_line2}
                    onChange={(e) => handleShippingChange('address_line2', e.target.value)}
                    placeholder="Suite 100"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.shipping_address.city}
                      onChange={(e) => handleShippingChange('city', e.target.value)}
                      placeholder="Los Angeles"
                      data-testid="contact-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.shipping_address.state}
                      onChange={(e) => handleShippingChange('state', e.target.value)}
                      placeholder="CA"
                      data-testid="contact-state"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">ZIP Code</Label>
                    <Input
                      id="postal_code"
                      value={formData.shipping_address.postal_code}
                      onChange={(e) => handleShippingChange('postal_code', e.target.value)}
                      placeholder="90001"
                      data-testid="contact-zip"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="delivery_notes">Delivery Notes</Label>
                  <Textarea
                    id="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={(e) => handleChange('delivery_notes', e.target.value)}
                    placeholder="Special delivery instructions..."
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes about this contact..."
              rows={3}
              data-testid="contact-notes"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="contact-submit-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateContactModal;
